import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  uid: string;
  email: string;
  role: 'admin' | 'whatsapp-only' | 'financial-only' | 'editor' | 'viewer';
}

interface AuthContextType {
  currentUser: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAdmin: () => boolean;
  isWhatsAppOnly: () => boolean;
  canViewFinancialInfo: () => boolean;
  canEditCadastros: () => boolean;
  canDeleteCadastros: () => boolean;
  canAccessReports: () => boolean;
  canAccessTempCadastros: () => boolean;
  getCurrentUserRole: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedUser = localStorage.getItem('currentUser');
    if (savedUser) {
      try {
        setCurrentUser(JSON.parse(savedUser));
      } catch (error) {
        console.error('Erro ao carregar usuario salvo:', error);
        localStorage.removeItem('currentUser');
      }
    }
    setLoading(false);
  }, []);

  const maskEmail = (email: string) => {
    const [name, domain] = email.trim().split('@');
    if (!domain) return 'email-invalido';
    return `${name.slice(0, 2)}***@${domain}`;
  };

  const login = async (email: string, password: string) => {
    try {
      const normalizedEmail = email.trim().toLowerCase();

      if (['admin@playkids.com.br', 'admin@colonia.com'].includes(normalizedEmail) && password === 'admin123') {
        const user: User = {
          uid: 'admin-1',
          email: normalizedEmail,
          role: 'admin'
        };
        setCurrentUser(user);
        localStorage.setItem('currentUser', JSON.stringify(user));
      } else if (email === 'whatsapp@playkids.com.br' && password === 'whatsapp123') {
        const user: User = {
          uid: 'whatsapp-1',
          email: email,
          role: 'whatsapp-only'
        };
        setCurrentUser(user);
        localStorage.setItem('currentUser', JSON.stringify(user));
      } else if (email === 'financeiro@playkids.com.br' && password === 'financeiro123') {
        const user: User = {
          uid: 'financeiro-1',
          email: email,
          role: 'financial-only'
        };
        setCurrentUser(user);
        localStorage.setItem('currentUser', JSON.stringify(user));
      } else if (email === 'editor@playkids.com.br' && password === 'editor123') {
        const user: User = {
          uid: 'editor-1',
          email: email,
          role: 'editor'
        };
        setCurrentUser(user);
        localStorage.setItem('currentUser', JSON.stringify(user));
      } else if (email === 'viewer@playkids.com.br' && password === 'viewer123') {
        const user: User = {
          uid: 'viewer-1',
          email: email,
          role: 'viewer'
        };
        setCurrentUser(user);
        localStorage.setItem('currentUser', JSON.stringify(user));
      } else {
        console.warn('[AUTH] Tentativa recusada', {
          email: maskEmail(email),
          reason: 'invalid_credentials',
          timestamp: new Date().toISOString()
        });
        return Promise.reject(new Error('Credenciais invalidas'));
      }
    } catch (error) {
      console.error('[AUTH] Erro ao processar login', {
        email: maskEmail(email),
        error: error instanceof Error ? error.message : String(error),
        timestamp: new Date().toISOString()
      });
      return Promise.reject(error);
    }
  };

  const logout = () => {
    setCurrentUser(null);
    localStorage.removeItem('currentUser');
  };

  const isAdmin = () => {
    return currentUser?.role === 'admin';
  };

  const isWhatsAppOnly = () => {
    return currentUser?.role === 'whatsapp-only';
  };

  const canViewFinancialInfo = () => {
    return ['admin', 'financial-only'].includes(currentUser?.role || '');
  };

  const canEditCadastros = () => {
    return ['admin', 'editor'].includes(currentUser?.role || '');
  };

  const canDeleteCadastros = () => {
    return currentUser?.role === 'admin';
  };

  const canAccessReports = () => {
    return ['admin', 'financial-only', 'viewer'].includes(currentUser?.role || '');
  };

  const canAccessTempCadastros = () => {
    return ['admin', 'financial-only'].includes(currentUser?.role || '');
  };

  const getCurrentUserRole = () => {
    return currentUser?.role || null;
  };

  const value: AuthContextType = {
    currentUser,
    login,
    logout,
    isAdmin,
    isWhatsAppOnly,
    canViewFinancialInfo,
    canEditCadastros,
    canDeleteCadastros,
    canAccessReports,
    canAccessTempCadastros,
    getCurrentUserRole
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
